import React, { useEffect, useState } from "react";
import { Navbar, MobileNav } from "@/components/Navbar";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ImageUpload } from "@/components/ImageUpload";
import { LocationSelect } from "@/components/LocationSelect";
import { PostcodeField } from "@/components/PostcodeField";
import { AccountControls } from "@/components/AccountControls";
import { ConnectedSocials } from "@/components/ConnectedSocials";
import { TermsAcceptedNote } from "@/components/TermsAcceptedNote";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { EmailNotificationToggle } from "@/components/EmailNotificationToggle";

export default function BusinessEdit() {
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [b, setB] = useState(null);

  useEffect(() => {
    Promise.all([api.get("/meta"), api.get("/business/profile/me")]).then(([m, prof]) => {
      setMeta(m.data);
      const d = prof.data;
      setB({
        business_name: d.business_name || "", logo: d.logo || "", category: d.category || "",
        location: d.location || "", website: d.website || "", instagram: d.instagram || "",
        tiktok: d.tiktok || "", description: d.description || "", target_customer: d.target_customer || "",
        postcode: d.postcode || "", county: d.county || "", lat: d.lat ?? null, lng: d.lng ?? null,
        location_source: d.location_source || "", publish_postcode: !!d.publish_postcode,
      });
    }).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/business/profile", b);
      toast.success("Business profile saved");
    } catch {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !b) return <div className="flex h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>;
  const card = "rounded-3xl border border-zinc-200 bg-white p-6";

  return (
    <div className="min-h-screen bg-[#FAF9F6] nf-pb-action">
      <Navbar />
      <div className="mx-auto max-w-2xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="font-display text-3xl font-bold tracking-tight text-zinc-900">Your business profile</h1>
          <Button data-testid="business-save-btn" onClick={save} disabled={saving} className="hidden rounded-full bg-emerald-600 hover:bg-emerald-700 md:inline-flex">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
          </Button>
        </div>

        <div className="space-y-6">
          <div className={card}>
            <Label>Business logo / photo</Label>
            <div className="mt-2"><ImageUpload value={b.logo} onChange={(v) => setB({ ...b, logo: v })} testid="business-logo-upload" /></div>
            <div className="mt-4"><Label>Business name</Label><Input data-testid="business-name" value={b.business_name} onChange={(e) => setB({ ...b, business_name: e.target.value })} className="mt-1.5" /></div>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div>
                <Label>Category</Label>
                <Select value={b.category} onValueChange={(v) => setB({ ...b, category: v })}>
                  <SelectTrigger data-testid="business-category" className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{meta.business_categories?.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Location</Label>
                <LocationSelect testid="business-location" value={b.location} onChange={(v) => setB({ ...b, location: v, postcode: "", county: "", lat: null, lng: null, location_source: "dropdown" })}
                  groups={meta.location_groups || []} placeholder="Select" />
              </div>
            </div>
            <div className="mt-4">
              <PostcodeField testid="business-postcode"
                value={{ postcode: b.postcode, town: b.location, county: b.county, lat: b.lat, lng: b.lng, source: b.location_source }}
                onChange={(loc) => setB({ ...b, postcode: loc.postcode, location: loc.town, county: loc.county, lat: loc.lat, lng: loc.lng, location_source: loc.source })} />
              <label className="mt-2 flex items-center gap-2 text-xs text-zinc-600">
                <input type="checkbox" data-testid="business-publish-postcode" checked={!!b.publish_postcode} onChange={(e) => setB({ ...b, publish_postcode: e.target.checked })} />
                Publish my full postcode publicly (off by default — only your town is shown to creators)
              </label>
            </div>
            <div className="mt-4"><Label>Website</Label><Input data-testid="business-website" value={b.website} onChange={(e) => setB({ ...b, website: e.target.value })} className="mt-1.5" placeholder="https://…" /></div>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div><Label>Instagram</Label><Input data-testid="business-instagram" value={b.instagram} onChange={(e) => setB({ ...b, instagram: e.target.value })} className="mt-1.5" placeholder="@handle" /></div>
              <div><Label>TikTok</Label><Input data-testid="business-tiktok" value={b.tiktok} onChange={(e) => setB({ ...b, tiktok: e.target.value })} className="mt-1.5" placeholder="@handle" /></div>
            </div>
            <div className="mt-4"><Label>Description</Label><Textarea data-testid="business-description" rows={3} value={b.description} onChange={(e) => setB({ ...b, description: e.target.value })} className="mt-1.5" placeholder="What does your business do?" /></div>
            <div className="mt-4"><Label>Target customer</Label><Input data-testid="business-target" value={b.target_customer} onChange={(e) => setB({ ...b, target_customer: e.target.value })} className="mt-1.5" placeholder="e.g. Families who love brunch" /></div>
          </div>
        </div>

        <div className="mt-8"><ConnectedSocials onUsePhoto={(url) => setB((s) => ({ ...s, logo: url }))} /></div>
        <TermsAcceptedNote />
        <div className="mt-8"><AccountControls /></div>
      </div>

      <EmailNotificationToggle />

      <div className="nf-actionbar-abovenav border-t border-zinc-200 bg-white p-3 md:hidden">
        <Button data-testid="business-save-btn-mobile" onClick={save} disabled={saving} className="h-12 w-full rounded-full bg-emerald-600 hover:bg-emerald-700">
          {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : "Save profile"}
        </Button>
      </div>
      <MobileNav />
    </div>
  );
}
